import type { IProductInventoryService } from './IProductInventoryService';
import type { ProductInventoryCreatePayload, ProductInventoryItemDto, ProductInventoryListParams } from '../dto';
import type { ProductInventoryOption } from '../model';
import type {
    IProductInventoryOptionMapper,
    IProductInventoryResponseMapper,
} from '../mapper';

import { ProductInventoryEndpoints } from './ProductInventoryEndpoints';
import {
    ProductInventoryOptionMapper,
    ProductInventoryResponseMapper,
} from '../mapper';

import { QueryBuilder } from '../http/QueryBuilder';
import { JsonRequestBody } from '../http/JsonRequestBody';

import { getRuntimeConfig } from '../../../utils/runtimeConfig';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { fetchWithAuth } from '../../../utils/fetch/FetchWithAuth';

export class ProductInventoryService implements IProductInventoryService {
    constructor(
        private readonly responseMapper: IProductInventoryResponseMapper =
            new ProductInventoryResponseMapper(),
        private readonly optionMapper: IProductInventoryOptionMapper =
            new ProductInventoryOptionMapper(),
    ) {}

    async list(params?: ProductInventoryListParams): Promise<unknown> {
        return fetchWithAuth(
            ProductInventoryEndpoints.list(QueryBuilder.from(params).build()),
            {
                method: 'GET',
            },
        );
    }

    async get(id: string): Promise<ProductInventoryItemDto> {
        return fetchWithAuth(ProductInventoryEndpoints.get(id), {
            method: 'GET',
        });
    }

    async create(
        payload: ProductInventoryCreatePayload,
    ): Promise<ProductInventoryItemDto> {
        const normalizedEan = payload.ean?.trim();

        const requestPayload: ProductInventoryCreatePayload = {
            ...payload,
            ean: normalizedEan || null,
            useAsComponent: payload.useAsComponent ?? false,
            includeComponents: payload.includeComponents ?? false,
        };

        return fetchWithAuth(ProductInventoryEndpoints.create(), {
            method: 'POST',
            ...JsonRequestBody.from(requestPayload),
        });
    }

    async update(
        id: string,
        payload: unknown,
    ): Promise<ProductInventoryItemDto> {
        return fetchWithAuth(ProductInventoryEndpoints.update(id), {
            method: 'PUT',
            ...JsonRequestBody.from(payload),
        });
    }

    async delete(id: string): Promise<void> {
        return fetchWithAuth(ProductInventoryEndpoints.delete(id), {
            method: 'DELETE',
        });
    }

    async activate(id: string): Promise<void> {
        return fetchWithAuth(ProductInventoryEndpoints.activate(id), {
            method: 'PUT',
        });
    }

    async deactivate(payload: unknown): Promise<void> {
        return fetchWithAuth(ProductInventoryEndpoints.deactivate(), {
            method: 'PUT',
            ...JsonRequestBody.from(payload),
        });
    }

    async getAllProducts(): Promise<ProductInventoryOption[]> {
        const response = await fetchWithAuth(
            ProductInventoryEndpoints.allProducts(),
            {
                method: 'GET',
            },
        );

        return this.responseMapper
            .toProductItems(response)
            .map(product => this.optionMapper.toOption(product))
            .filter((option): option is ProductInventoryOption => option !== null);
    }

    async getStatusList(): Promise<unknown> {
        return fetchWithAuth(ProductInventoryEndpoints.statuses(), {
            method: 'GET',
        });
    }

    async getUnitOfMeasurementList(): Promise<unknown> {
        return fetchWithAuth(ProductInventoryEndpoints.unitOfMeasurementList(), {
            method: 'GET',
        });
    }

    async getVatCodeList(): Promise<unknown> {
        return fetchWithAuth(ProductInventoryEndpoints.vatCodeList(), {
            method: 'GET',
        });
    }

    async getCategoryNames(): Promise<unknown> {
        return fetchWithAuth(ProductInventoryEndpoints.categoryNames(), {
            method: 'GET',
        });
    }

    async getCurrencyList(): Promise<unknown> {
        return fetchWithAuth(ProductInventoryEndpoints.currencies(), {
            method: 'GET',
        });
    }

    async checkEanAvailability(ean: string): Promise<unknown> {
        return fetchWithAuth(ProductInventoryEndpoints.eanAvailability(ean), {
            method: 'GET',
        });
    }

    async getPlanogramProductsWithHasPhoto(): Promise<unknown> {
        return fetchWithAuth(
            ProductInventoryEndpoints.planogramProductsWithHasPhoto(),
            {
                method: 'GET',
            },
        );
    }

    getPhotoUrl(ean: string | null | undefined): string | null {
        try {
            const domain = getRuntimeConfig('REACT_APP_BACKEND_URL');

            return ean
                ? `${domain}${ProductInventoryEndpoints.photo(ean)}`
                : null;
        } catch {
            return null;
        }
    }

}